import { PlatformLocation } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewContainerRef,
  output,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import type { CurrentUser } from '@core/account-types';
import { FullCandidateActions } from '@core/candidate-data';
import type { FullCandidate } from '@core/candidate-types';
import {
  CandidateSharedWithListComponent,
  UsersGroupedByAccessKindListComponent,
} from '@core/candidate-ui';
import { ShareCandidatesAnalytics } from '@core/candidate-util';
import { trackByValue } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { HelpArticlesDirective } from '@core/help-articles-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { CandidateSharedWith, CandidateShareUserGroup } from '@core/sharing-container-types';
import { TELLENT_AUTH_ORG_GUID_PARAM } from '@core/tellent-client-logic';
import { injectIsOneDomain } from '@core/tellent-util';
import {
  ButtonComponent,
  ClipboardCopy,
  DialogService,
  DividerComponent,
  HeadingComponent,
  IconComponent,
  NewItemButtonComponent,
  RtTooltip,
  WindowRef,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-candidate-sharing-team-members-tab',
  imports: [
    NewItemButtonComponent,
    DividerComponent,
    ButtonComponent,
    IconComponent,
    HeadingComponent,
    TranslatePipe,
    CandidateSharedWithListComponent,
    RtTooltip,
    HelpArticlesDirective,
    InfoboxComponent,
    UsersGroupedByAccessKindListComponent,
  ],
  templateUrl: './candidate-sharing-team-members-tab.component.html',
  styleUrls: ['./candidate-sharing-team-members-tab.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateSharingTeamMembersTabComponent {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly dialogSvc = inject(DialogService);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly windowRef = inject(WindowRef);
  private readonly clipboardCopy = inject(ClipboardCopy);
  private readonly i18n = inject(I18nService);
  private readonly shareCandidatesAnalytics = inject(ShareCandidatesAnalytics);
  private readonly actions$ = inject(Actions);

  #isOneDomain = injectIsOneDomain();
  #platformLocation = inject(PlatformLocation);

  public readonly candidate = input<FullCandidate>(undefined!);
  public readonly currentUser = input<CurrentUser>(undefined!);
  public readonly companyId = input<number>(undefined!);
  public readonly sharingDisabled = input(false);
  public readonly usersAlreadyWithAccess = input<CandidateShareUserGroup[]>([]);
  @Input() public sharedWithUsers: CandidateSharedWith[] = [];
  public whoHasAccessExpanded: boolean = false;
  public isWhoHasAccessHidden: boolean = false;

  public trackByValue = trackByValue;

  public newItemClick = output<void>();

  private trackRemoveTeamMember$ = this.actions$.pipe(
    ofType(FullCandidateActions.revokeUserAccessSuccess),
    tap(() => this.shareCandidatesAnalytics.trackRemoveTeamMember()),
    takeUntilDestroyed(),
  );

  constructor() {
    this.trackRemoveTeamMember$.subscribe();
  }

  public toggleAccessExpanded() {
    this.whoHasAccessExpanded = !this.whoHasAccessExpanded;
    this.cdRef.detectChanges();
  }

  public handleStopSharing(user: CandidateSharedWith) {
    this.dialogSvc
      .error(
        {
          title: this.i18n.translate(
            'app.candidate.share_dialog.team_members.revoke_access.title',
            { firstName: user.firstName },
          ),
          message: this.i18n.translate(
            'app.candidate.share_dialog.team_members.revoke_access.description',
            { fullName: `${user.firstName} ${user.lastName}` },
          ),
          agreeLabel: this.i18n.translate(
            'app.candidate.share_dialog.team_members.revoke_access.agree_label',
          ),
        },
        this.vcRef,
      )
      .pipe(
        take(1),
        filter(shouldRevokeAccess => shouldRevokeAccess),
      )
      .subscribe(() =>
        this.store.dispatch(
          FullCandidateActions.revokeUserAccess({
            payload: {
              candidateId: this.candidate().id,
              userId: user.adminId,
              admins: this.candidate().admins || [],
            },
          }),
        ),
      );
  }

  public copyLink() {
    const url = this.#isOneDomain
      ? this.getCandidateLinkForOneDomain()
      : this.getCandidateLinkForLegacyDomain();

    this.clipboardCopy.fromString(
      url,
      this.i18n.translate('app.candidate.share_dialog.team_members.link_copied'),
    );
  }

  public markFeatureDiscoveryAsUsed() {
    this.store.dispatch(
      FeatureDiscoveryActions.update({ payload: { shareCandidateOnboardingUsed: true } }),
    );
  }

  private getCandidateLinkForOneDomain() {
    const { origin } = this.windowRef.nativeWindow.location;

    const appUrlWithBase = `${origin}${this.#platformLocation.getBaseHrefFromDOM()}`;

    const candidateUrl = new URL(`${appUrlWithBase}candidates`);

    candidateUrl.searchParams.append('candidate', `${this.candidate().id}`);
    candidateUrl.searchParams.append(
      TELLENT_AUTH_ORG_GUID_PARAM,
      new URLSearchParams(this.#platformLocation.search).get(TELLENT_AUTH_ORG_GUID_PARAM)!,
    );

    return candidateUrl.toString();
  }

  private getCandidateLinkForLegacyDomain() {
    const { port: _port, hostname, protocol } = this.windowRef.nativeWindow.location;
    const port = _port ? `:${_port}` : '';
    const location = 'candidates';
    const queryParams = `?candidate=${this.candidate().id}&company=${this.companyId()}`;
    const url = `${protocol}//${hostname}${port}/#/${location}${queryParams}`;

    return url;
  }
}
